import { useEffect, useMemo, useCallback, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { AlertCircle, Save } from "lucide-react";
import { ProductEntry, FootprintProduct } from "@/types/product/productStep.types";
import { useData, useDataWithFilter } from "@/hooks/useData";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { CommonFormFields } from "./shared/CommonFormFields";
import { TransportLegsField } from "./shared/TransportLegsField";
import { normalizeProductTransports } from "./shared/transportUtils";
import { useProductFormValidation } from "./shared/useProductFormValidation";
import { calculateTransformationProductFootprints } from "@/components/footprints/utils/calculations/transformation";
import {
  calculateProductRevenue,
  calculateRatioByQuantity,
  calculateRatioByRevenue,
} from "@/components/footprints/utils/calculations/extraction";
import { convertToTonnes } from "@/components/footprints/utils/calculations/unitConversions";
import { MaterialDocWithId } from "@/types/material";

interface TransformationProductFormProps {
  formEntry: ProductEntry;
  onSave: (entry: ProductEntry) => void;
  onClose: () => void;
  editing: boolean;
  calculationInputs: any;
  allEntries: ProductEntry[];
  totalAnnualProduction: number;
  totalRevenue: number;
  formData?: any;
  extractionProducts: ProductEntry[]; // Local extraction products
  currentCompanyId?: string; // Current company ID
}

export default function TransformationProductForm({
  formEntry: formEntryProp,
  onSave,
  onClose,
  editing,
  calculationInputs,
  allEntries,
  totalAnnualProduction,
  totalRevenue,
  formData,
  extractionProducts,
  currentCompanyId,
}: TransformationProductFormProps) {
  const { t } = useLanguage();

  const [formEntry, setFormEntry] = useState<ProductEntry>(() => normalizeProductTransports(formEntryProp));

  const { touched, errors, validateForm, handleFieldBlur, clearFieldError, isFormValid, markAllFieldsTouched } =
    useProductFormValidation("transformation");

  // Fetch stone types for the dropdown
  const { data: stoneTypes } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  // Fetch companies for transformation origin selection
  const { data: companies } = useData<any>({
    collectionName: "companies",
  });

  // Fetch transports
  const { data: transportDistributions } = useData<any>({
    collectionName: "setup_transportDistributions",
  });

  // Fetch footprints for the selected company (to get footprint_ids)
  const { data: companyFootprints } = useDataWithFilter<any>({
    collectionName: "footprints",
    filters: formEntry.originCompanyId
      ? [{ field: "company_id", operator: "==", value: formEntry.originCompanyId }]
      : undefined,
  });

  // Get footprint IDs for the selected company
  const footprintIds = useMemo(() => {
    return companyFootprints?.map((footprint) => footprint.id) || [];
  }, [companyFootprints]);

  // Fetch products from the footprints of the selected company that match the stone type
  const { data: availableOriginProducts } = useDataWithFilter<FootprintProduct>({
    collectionName: "footprint_products",
    filters:
      footprintIds.length > 0 && formEntry.stoneType
        ? [
            { field: "footprint_id", operator: "in", value: footprintIds },
            { field: "stone_type_id", operator: "==", value: formEntry.stoneType },
            { field: "product_flow", operator: "==", value: "extraction" },
          ]
        : undefined,
  });

  // Merge local extraction products with Firestore products
  const mergedOriginProducts = useMemo(() => {
    const firestoreProducts = availableOriginProducts || [];

    // Filter local extraction products that match the selected stone type
    const localProducts = (extractionProducts || [])
      .filter((product) => product.stoneType === formEntry.stoneType)
      .map((product) => ({
        id: product.id,
        name: product.name,
        product_type: product.productType,
        functional_unit: product.functionalUnit,
        stone_type_id: product.stoneType,
        stone_type_name: product.stoneTypeName,
        product_flow: product.productFlow as "extraction",
        isLocal: true, // Mark as local/unsaved
      }));

    // Combine Firestore and local products, avoiding duplicates by ID
    const combinedProducts = [...firestoreProducts];
    localProducts.forEach((localProduct) => {
      const existsInFirestore = firestoreProducts.some((fp) => fp.id === localProduct.id);
      if (!existsInFirestore) {
        combinedProducts.push(localProduct as any);
      }
    });

    return combinedProducts;
  }, [availableOriginProducts, extractionProducts, formEntry.stoneType]);

  // Filter active data
  const activeStoneTypes = stoneTypes || [];
  const activeCompanies = (companies || []).filter((company: any) => !company.isArchived);
  const activeTransportDistributions = (transportDistributions || []).filter((transport: any) => !transport.isArchived);

  // Filter companies by activity type (only extraction companies for stone origin)
  // Exclude current company from the list
  const extractionCompanies = activeCompanies.filter(
    (company: any) =>
      company.id !== currentCompanyId &&
      (company.activityType === "extraction" || company.activityType === "extraction_and_transformation")
  );

  // Get stone type data
  const selectedStoneType: StoneType | null = activeStoneTypes.find((st) => st.id === formEntry.stoneType) || null;

  // Get stone density
  const stoneDensity = selectedStoneType?.dens_pedra?.value || 0;

  // Update density when stone type changes
  useEffect(() => {
    if (formEntry.density !== stoneDensity) {
      setFormEntry((prev) => ({
        ...prev,
        density: stoneDensity,
      }));
    }
  }, [stoneDensity, formEntry.density]);

  // Ensure stone type name is populated for existing entries
  useEffect(() => {
    if (formEntry.stoneType && !formEntry.stoneTypeName && activeStoneTypes.length > 0) {
      const selectedStone = activeStoneTypes.find((st) => st.id === formEntry.stoneType);
      if (selectedStone && selectedStone.name !== formEntry.stoneTypeName) {
        setFormEntry((prev) => ({
          ...prev,
          stoneTypeName: selectedStone.name,
        }));
      }
    }
  }, [activeStoneTypes.length, formEntry.stoneType, formEntry.stoneTypeName]);

  // Automatically set stoneOrigin to average_factors for transformation companies
  useEffect(() => {
    if (!formEntry.stoneOrigin || formEntry.stoneOrigin !== "average_factors") {
      setFormEntry((prev) => ({
        ...prev,
        stoneOrigin: "average_factors",
      }));
    }
  }, []);

  // Clear origin product when company changes
  useEffect(() => {
    // Only clear if mergedOriginProducts is loaded and does not contain the value
    if (formEntry.originProductId && Array.isArray(mergedOriginProducts) && mergedOriginProducts.length > 0) {
      const stillExists = mergedOriginProducts.some(
        (product) => String(product.id) === String(formEntry.originProductId)
      );
      if (!stillExists) {
        setFormEntry((prev) => ({
          ...prev,
          originProductId: "",
        }));
      }
    }
  }, [formEntry.originCompanyId, mergedOriginProducts]);

  const handleInputChange = (field: string, value: any) => {
    if (field === "stoneType") {
      const selectedStone = activeStoneTypes.find((st) => st.id === value);
      setFormEntry((prev) => ({
        ...prev,
        stoneType: value,
        stoneTypeName: selectedStone?.name || "",
      }));
    } else if (field === "originCompanyId") {
      // Clear origin product when company changes
      setFormEntry((prev) => ({
        ...prev,
        [field]: value,
        originProductId: "",
      }));
    } else {
      setFormEntry((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const onFieldBlur = useCallback(
    (field: string, value?: any) => {
      // If a value is provided, create a temporary formEntry for validation
      if (value !== undefined) {
        const tempFormEntry = { ...formEntry, [field]: value };
        handleFieldBlur(field, tempFormEntry);
      } else {
        handleFieldBlur(field, formEntry);
      }
    },
    [formEntry, handleFieldBlur]
  );

  // Extract available material IDs from annual consumption data
  const getAvailableMaterialIds = (): string[] => {
    if (!formData) return [];

    const activityType = calculationInputs?.generalData?.activityType;
    let materialEntries: any[] = [];

    if (activityType === "extraction_and_transformation") {
      // Mixed company - check transformation materials
      materialEntries = formData.annualConsumptions?.transformation?.materials?.entries || [];
    } else if (activityType === "transformation") {
      // Single transformation company - check both possible structures
      materialEntries =
        formData.materials?.entries || formData.annualConsumptions?.transformation?.materials?.entries || [];
    }

    // Extract material IDs from material entries (check multiple possible field names)
    const materialIds = materialEntries
      .filter((entry: any) => {
        // Only include entries that have a material type/name (actual materials, not empty entries)
        return entry.materialType && entry.materialType.trim() !== "";
      })
      .map((entry: any) => {
        // For materials, we need to find the setup_item_id from the material name
        // This will be resolved when we fetch the material document
        return entry.materialType;
      });

    return materialIds;
  };

  const { data: materialDocs } = useData<MaterialDocWithId>({
    collectionName: "setup_materials",
  });
  const { data: stoneDocs } = useData<StoneType>({
    collectionName: "setup_stoneTypes",
  });

  const handleSave = () => {
    markAllFieldsTouched(["transports"]);
    if (validateForm(formEntry) && materialDocs && stoneDocs) {
      // Calculate the product's total annual production in tonnes
      const annualProductionInTonnes = convertToTonnes(formEntry).value;

      // Calculate ratios using the correct total production
      const calculatedProductRevenue = calculateProductRevenue(
        formEntry.annualProduction,
        totalRevenue,
        totalAnnualProduction
      );
      const ratioByQuantity = calculateRatioByQuantity(annualProductionInTonnes, totalAnnualProduction);
      const ratioByRevenue = calculateRatioByRevenue(formEntry.productRevenue, totalRevenue);

      // Calculate decision ratio (rd) based on transformation PVP values
      const pvpCaro = calculationInputs?.generalData?.pvp_caro_trsf || 0;
      const pvpBarato = calculationInputs?.generalData?.pvp_barato_trsf || 0;
      const decisionRatio = pvpBarato > 0 ? pvpCaro / pvpBarato : Infinity;
      const allocationType = decisionRatio <= 5 ? "physical" : "economic";

      // Auto-allocate material quantities based on annual consumption and allocation type
      // Helper to find annual material entry and products using a material
      const getAnnualMaterialData = (material: any) => {
        const activityType = calculationInputs?.generalData?.activityType;
        let materialEntries: any[] = [];

        if (activityType === "extraction_and_transformation") {
          materialEntries = formData?.annualConsumptions?.transformation?.materials?.entries || [];
        } else if (activityType === "transformation") {
          materialEntries =
            formData?.materials?.entries || formData?.annualConsumptions?.transformation?.materials?.entries || [];
        }

        const annualMaterialEntry = materialEntries.find((entry: any) => {
          const byId = entry?.setup_item_id === material.materialId;
          const bySetupName = (entry?.setup_item_name || "").toLowerCase() === (material.materialName || "").toLowerCase();
          const byType = (entry?.materialType || "").toLowerCase() === (material.materialName || "").toLowerCase();
          const byName = (entry?.materialName || "").toLowerCase() === (material.materialName || "").toLowerCase();
          return byId || bySetupName || byType || byName;
        });

        const entriesForAllocation = editing ? allEntries : [...allEntries, formEntry];
        const productsUsingMaterial = entriesForAllocation.filter(
          (entry) =>
            entry.productFlow === "transformation" &&
            entry.materialsUsed.some(
              (mat) => mat.materialId === material.materialId || mat.materialName === material.materialName
            )
        );

        return { annualMaterialEntry, productsUsingMaterial };
      };

      // Allocate materials for carbon (rd-based allocation)
      const materialsWithQuantities = formEntry.materialsUsed.map((material) => {
        const { annualMaterialEntry, productsUsingMaterial } = getAnnualMaterialData(material);

        if (annualMaterialEntry && annualMaterialEntry.quantity !== undefined) {
          if (productsUsingMaterial.length === 0) {
            return { ...material, quantity: 0, unit: annualMaterialEntry.unit || material.unit };
          }

          let totalForMaterial = 0;
          let currentProductValue = 0;

          if (allocationType === "physical") {
            totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + convertToTonnes(entry).value, 0);
            currentProductValue = annualProductionInTonnes;
          } else {
            totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);
            currentProductValue = Number(formEntry.productRevenue) || 0;
          }

          const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
          const allocatedQuantity = Number(annualMaterialEntry.quantity) * materialSpecificRatio;

          return { ...material, quantity: allocatedQuantity, unit: annualMaterialEntry.unit || material.unit };
        }
        return material;
      });

      // Allocate materials for hydric (ALWAYS economic allocation)
      const materialsUsedHydric = allocationType === "economic"
        ? materialsWithQuantities
        : formEntry.materialsUsed.map((material) => {
            const { annualMaterialEntry, productsUsingMaterial } = getAnnualMaterialData(material);

            if (annualMaterialEntry && annualMaterialEntry.quantity !== undefined) {
              if (productsUsingMaterial.length === 0) {
                return { ...material, quantity: 0, unit: annualMaterialEntry.unit || material.unit };
              }

              // Always use economic (revenue-based) allocation for hydric
              const totalForMaterial = productsUsingMaterial.reduce((sum, entry) => sum + (Number(entry.productRevenue) || 0), 0);
              const currentProductValue = Number(formEntry.productRevenue) || 0;
              const materialSpecificRatio = totalForMaterial > 0 ? currentProductValue / totalForMaterial : 0;
              const allocatedQuantity = Number(annualMaterialEntry.quantity) * materialSpecificRatio;

              return { ...material, quantity: allocatedQuantity, unit: annualMaterialEntry.unit || material.unit };
            }
            return material;
          });

      const entryWithRatios = {
        ...formEntry,
        materialsUsed: materialsWithQuantities,
        materialsUsedHydric,
        calculatedProductRevenue,
        ratioByQuantity,
        ratioByRevenue,
        rendimento: calculationInputs.generalData.rend,
      };

      const footprints = calculateTransformationProductFootprints(
        entryWithRatios,
        allEntries,
        calculationInputs,
        stoneDocs,
        transportDistributions,
        materialDocs
      );

      const mappedEntry = {
        ...entryWithRatios,
        ...footprints,
        rendimento: calculationInputs.generalData.rend,
        carbonFootprintMaterials: footprints.pc_trsf_materiais,
        carbonFootprintElectricity: footprints.pc_trsf_ecr,
        carbonFootprintElectricityOnly: footprints.pc_ele || 0,
        carbonFootprintFuels: footprints.pc_comb || 0,
        carbonFootprintResidue: footprints.pc_res || 0,
        carbonFootprintTotal: footprints.carbonFootprintTotal,
        hydricFootprintMaterials: footprints.ph_trsf_materiais,
        hydricFootprintElectricity: footprints.ph_trsf_ec,
        hydricFootprintWater: footprints.ph_trsf_agua,
        hydricFootprintTotal: footprints.hydricFootprintTotal,
        carbonFootprintTransport: footprints.pc_trans_total,
        hydricFootprintTransport: footprints.ph_trans_total,
        pc_ext_total: footprints.pc_ext_total,
        ph_ext_total: footprints.ph_ext_total,
      };
      onSave(mappedEntry);
    }
  };

  return (
    <div className="space-y-6 py-4">
      {/* Stone Type */}
      <div className="space-y-2">
        <Label htmlFor="stoneType" className={`font-medium ${errors.stoneType ? "text-red-500" : "text-gray-700"}`}>
          {t("footprints.generalData.stoneTypeTransformation")} *
        </Label>
        <SearchableSelect
          value={formEntry.stoneType}
          onChange={(value) => handleInputChange("stoneType", value)}
          onBlur={(value) => onFieldBlur("stoneType", value)}
          options={activeStoneTypes.map((stoneType) => ({
            value: stoneType.id,
            label: stoneType.name,
          }))}
          placeholder={t("footprints.generalData.selectStoneType")}
          className={touched.stoneType && errors.stoneType ? "border-red-500" : ""}
        />
        {touched.stoneType && errors.stoneType && (
          <div className="flex items-center gap-1 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{errors.stoneType}</span>
          </div>
        )}
      </div>

      {/* Stone Origin Question */}
      <div className="space-y-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <h4 className="text-sm font-medium text-blue-800">{t("products.stoneOriginData")}</h4>
        </div>

        {/* Hidden for now - keep code for future use */}
        {false && (
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <div className="w-1 h-4 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
              <div className="flex-1">
                <Label className="font-medium text-gray-800 text-base">
                  {t("products.stoneFromAssociatedCompany")} *
                </Label>
              </div>
            </div>
            <div className="flex gap-6 ml-6">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="stoneOrigin"
                  value="associated_company"
                  checked={formEntry.stoneOrigin === "associated_company"}
                  onChange={(e) => handleInputChange("stoneOrigin", e.target.value)}
                  onBlur={() => onFieldBlur("stoneOrigin")}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{t("common.yes")}</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="radio"
                  name="stoneOrigin"
                  value="average_factors"
                  checked={formEntry.stoneOrigin === "average_factors"}
                  onChange={(e) => handleInputChange("stoneOrigin", e.target.value)}
                  onBlur={() => onFieldBlur("stoneOrigin")}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{t("common.no")}</span>
              </label>
            </div>
            {touched.stoneOrigin && errors.stoneOrigin && (
              <div className="flex items-center gap-1 text-red-500 text-sm ml-6">
                <AlertCircle className="h-4 w-4" />
                <span>{errors.stoneOrigin}</span>
              </div>
            )}
          </div>
        )}

        {/* Always show average factors message */}
        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg ml-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-yellow-800">{t("products.willUseAverageFactors")}</p>
              <p className="text-xs text-yellow-600 mt-1">{t("products.willUseAverageFactorsDescription")}</p>
            </div>
          </div>
        </div>

        {/* Hidden for now - keep code for future use */}
        {false && formEntry.stoneOrigin === "associated_company" && (
          <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl ml-4">
            {/* Company Selection */}
            <div className="space-y-2">
              <Label className="font-medium text-gray-700">{t("products.selectOriginCompany")} *</Label>
              <p className="text-xs text-gray-500">{t("products.stoneFromAssociatedCompanyLabel")}</p>
              <SearchableSelect
                value={formEntry.originCompanyId || ""}
                onChange={(value) => handleInputChange("originCompanyId", value)}
                onBlur={(value) => onFieldBlur("originCompanyId", value)}
                options={extractionCompanies.map((company: any) => ({
                  value: company.id,
                  label: company.name,
                }))}
                placeholder={extractionCompanies.length > 0 ? t("products.selectCompany") : t("common.loading")}
                disabled={extractionCompanies.length === 0}
                className={touched.originCompanyId && errors.originCompanyId ? "border-red-500" : ""}
              />
              {touched.originCompanyId && errors.originCompanyId && (
                <div className="flex items-center gap-1 text-red-500 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  <span>{errors.originCompanyId}</span>
                </div>
              )}
              {extractionCompanies.length === 0 && (
                <p className="text-xs text-gray-500">{t("products.noExtractionCompaniesAvailable")}</p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleInputChange("stoneOrigin", "average_factors")}
                className="mt-2 text-xs"
              >
                {t("products.noCompanyFound")}
              </Button>
            </div>

            {/* Product Selection - Only show if company is selected */}
            {formEntry.originCompanyId && (
              <div className="space-y-2">
                <Label className="font-medium text-gray-700">{t("products.selectOriginProduct")} *</Label>
                {mergedOriginProducts && mergedOriginProducts.length > 0 ? (
                  <SearchableSelect
                    value={String(formEntry.originProductId) || ""}
                    onChange={(value) => handleInputChange("originProductId", value)}
                    onBlur={(value) => onFieldBlur("originProductId", value)}
                    options={mergedOriginProducts.map((product) => ({
                      value: String(product.id),
                      label: `${product.name} - ${product.product_type} (${product.functional_unit.quantity} ${product.functional_unit.unit})`,
                    }))}
                    placeholder={t("products.selectProduct")}
                    className={touched.originProductId && errors.originProductId ? "border-red-500" : ""}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      {footprintIds.length === 0
                        ? t("products.noProductsRegistered")
                        : formEntry.stoneType
                        ? t("products.noProductsForStoneType", { stoneType: selectedStoneType?.name || "" })
                        : t("products.selectStoneTypeFirst")}
                    </p>
                  </div>
                )}
                {touched.originProductId && errors.originProductId && (
                  <div className="flex items-center gap-1 text-red-500 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{errors.originProductId}</span>
                  </div>
                )}
                <p className="text-xs text-gray-500">{t("products.originProductHelp")}</p>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Transport Information - Always show for transformation products */}
      <div className="space-y-4 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
          <h4 className="text-sm font-medium text-orange-800">{t("products.transportInformation")}</h4>
        </div>
        <p className="text-xs text-orange-600">{t("products.transportInformationDescription")}</p>

        <TransportLegsField
          transports={formEntry.transports || []}
          vehicleOptions={activeTransportDistributions.map((tr: any) => ({
            value: tr.vehicleType,
            label: tr.vehicleType,
          }))}
          loading={!transportDistributions}
          showError={!!touched.transports}
          error={errors.transports}
          onChange={(transports) => {
            const updated = { ...formEntry, transports };
            setFormEntry(updated);
            if (touched.transports) clearFieldError("transports", transports, updated);
          }}
          onBlur={() => handleFieldBlur("transports", formEntry)}
        />
      </div>

      {/* Common Form Fields */}
      <CommonFormFields
        formEntry={formEntry}
        onFormEntryChange={setFormEntry}
        touched={touched}
        errors={errors}
        onFieldBlur={onFieldBlur}
        activityType="transformation"
        availableMaterialIds={getAvailableMaterialIds()}
      />

      {/* Dialog Footer */}
      <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
        <Button variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          disabled={
            !isFormValid(formEntry) ||
            !(
              formEntry.transports &&
              formEntry.transports.length > 0 &&
              formEntry.transports.every((leg) => !!leg.vehicleType && (Number(leg.distance) || 0) > 0)
            )
          }
        >
          <Save className="w-4 h-4 mr-2" />
          {editing ? t("common.saveChanges") : t("common.add")}
        </Button>
      </div>
    </div>
  );
}
